export const locales = ["en", "zh"] as const

export const defaultLocale = "en" as const satisfies AppLocale

export type AppLocale = (typeof locales)[number]

/**
 * Locales written right-to-left. Add new ones here only — every dir-aware
 * consumer (`<html dir>`, Radix roots, motion offsets) reads this. The SaaS
 * site ships en + zh only, so no RTL locale is configured.
 */
export const rtlLocales: readonly AppLocale[] = []

export type LocaleDirection = "ltr" | "rtl"

export const getLocaleDir = (locale: string): LocaleDirection =>
  (rtlLocales as readonly string[]).includes(locale) ? "rtl" : "ltr"

export type Locale = {
  code: string
  name: string
}

export const localeLabels: Record<AppLocale, string> = {
  en: "English",
  zh: "中文",
}

export const appLocales: Locale[] = locales.map((code) => ({
  code,
  name: localeLabels[code],
}))

export function isAppLocale(
  value: string | null | undefined
): value is AppLocale {
  return !!value && locales.includes(value as AppLocale)
}

/** 任意字符串 → 受支持的 locale，认不出就回默认（给 `getLocale()` 的结果兜底）。 */
export function toAppLocale(value: string | null | undefined): AppLocale {
  return isAppLocale(value) ? value : defaultLocale
}

export function matchBrowserLocale(
  acceptLanguage: string | null | undefined
): AppLocale | undefined {
  if (!acceptLanguage) return undefined

  const primary = acceptLanguage
    .split(",")[0]
    .trim()
    .split(";")[0]
    .split("-")[0]
    .toLowerCase()

  return isAppLocale(primary) ? primary : undefined
}
